import React from 'react';
import { useSocialContext } from './social';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
const JWT_KEY = 'drupi_social_jwt';

const authHeaders = (): Record<string, string> => {
  const jwt = localStorage.getItem(JWT_KEY);
  return jwt ? { Authorization: `Bearer ${jwt}` } : {};
};

export interface PaymentProfile {
  full_name: string;
  email: string;
  paypal_account: string;
  mercadopago_alias: string;
  country: string;
  /** 'mercadopago' | 'bank_transfer' | '' (todavía no eligió). */
  payment_method: string;
  bank_name: string;
  bank_account_number: string;
  bank_holder_document: string;
  terms_accepted: boolean;
  adult_confirmed: boolean;
  /** true = lo ESENCIAL está (nombre + email válido + términos + mayor de edad; lo
   *  calcula el backend). Gobierna el popup bloqueante del Home. */
  complete: boolean;
  /** true = país + método de cobro completos (Mercado Pago → alias; transferencia →
   *  banco + cuenta + documento; lo calcula el backend). Gobierna el cartel ⚠️ de
   *  "faltan datos para el pago" del header/Settings, NO el popup. */
  payment_data_complete: boolean;
  /** true = el email fue verificado con el código (identidad por email, 2026-07-31).
   *  `complete && !email_verified` → la app entera queda bloqueada en la pantalla
   *  "Verificá tu email" (ver components/email-verification-gate.tsx). */
  email_verified: boolean;
}

export type PaymentProfileFields = Partial<
  Pick<
    PaymentProfile,
    | 'full_name' | 'email' | 'paypal_account' | 'mercadopago_alias'
    | 'country' | 'payment_method' | 'bank_name' | 'bank_account_number'
    | 'bank_holder_document' | 'terms_accepted' | 'adult_confirmed'
  >
>;

interface PaymentProfileState {
  profile: PaymentProfile | null;
  loading: boolean;
  complete: boolean;
  save: (fields: PaymentProfileFields) => Promise<PaymentProfile>;
}

const Ctx = React.createContext<PaymentProfileState>({
  profile: null,
  loading: true,
  complete: false,
  save: async () => { throw new Error('PaymentProfileProvider ausente'); },
});

export const PaymentProfileProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { isAuthenticated } = useSocialContext();
  const [profile, setProfile] = React.useState<PaymentProfile | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (!isAuthenticated) { setLoading(false); return; }
    let alive = true;
    setLoading(true);
    fetch(`${API_BASE}/drupi/user-profile`, { headers: authHeaders() })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (alive && d) setProfile(d); })
      .catch(() => { /* best-effort: sin perfil, el gate lo pide */ })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [isAuthenticated]);

  const save = React.useCallback(async (fields: PaymentProfileFields) => {
    const r = await fetch(`${API_BASE}/drupi/user-profile`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(fields),
    });
    if (!r.ok) {
      // El backend responde con motivos concretos (409 "ese email ya está en uso",
      // 422 formato inválido) — mostrarlos, no un genérico que deja al usuario ciego.
      let detail = '';
      try {
        const err = await r.json();
        if (typeof err?.detail === 'string') detail = err.detail;
      } catch { /* body no-JSON → cae al genérico */ }
      throw new Error(detail || 'No se pudieron guardar los datos');
    }
    const d: PaymentProfile = await r.json();
    setProfile(d);
    return d;
  }, []);

  return (
    <Ctx.Provider value={{ profile, loading, complete: !!profile?.complete, save }}>
      {children}
    </Ctx.Provider>
  );
};

export const usePaymentProfile = () => React.useContext(Ctx);
